import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { removeShortlist } from '@store/actionSlices/shortlist'
import { RouteKey } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

import { useLazyGetConfigQuery } from '@api/config'

import useDefaultRoom from '@utilities/use-default-room'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { Project } from './types'

const useProjectSwitchUtils = () => {
  const dispatch = useDispatch()

  const projectIdentity = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity
  )
  const [getProjectConfig] = useLazyGetConfigQuery()

  const { getDefaultRoom, setDefaultRoom } = useDefaultRoom()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const triggerFirebaseProjectSwitch = useCallback(
    async ({
      projectId,
      projectLabel,
      projectName,
    }: {
      projectId: string
      projectLabel: string
      projectName: string
    }) => {
      await firebaseControlQuery.update({
        [`projectSwitch`]: {
          projectId,
          projectLabel,
          projectName,
        },
      })
    },
    []
  )

  const getProject = useCallback(
    (project: Project) => {
      const newSessionList = projectIdentity.sessionList.map((session) => {
        if (session.projectId === project.canonicalName) {
          return {
            ...session,
            projectName: session.projectName || project.canonicalName,
            projectUUID: session.projectUUID || project.uuid,
          }
        }
        return session
      })
      const sessionProject = newSessionList.find(
        (session) => session.projectId === project.canonicalName
      )
      return { sessionProject, newSessionList }
    },
    [projectIdentity.sessionList]
  )

  const validateRedirectPath = useCallback<(path: string) => boolean>(
    (path) => {
      const routes = Object.values(RouteKey)
      return routes.includes(path as RouteKey)
    },
    []
  )

  const startProjectSwitch = useCallback<
    (args: { project: Project; redirectTo?: string }) => Promise<{
      status: boolean
      message?: string
    }>
  >(
    async ({ project, redirectTo }) => {
      const { sessionProject, newSessionList } = getProject(project)

      if (!sessionProject) {
        return {
          status: false,
          message: 'There are no sessions for the selected project.',
        }
      }

      const projectName = sessionProject.projectId || project.canonicalName
      const defaultRoom = await getDefaultRoom({
        projectName,
        prioritizeCurrentRoom: true,
      })
      if (defaultRoom) {
        setDefaultRoom(defaultRoom)
      }

      await triggerFirebaseProjectSwitch({
        projectId: sessionProject.projectUUID || project.uuid,
        projectName,
        projectLabel: sessionProject.projectName || project.canonicalName,
      })

      localStorage.setItem(
        'projectIdentity',
        JSON.stringify({
          ...projectIdentity,
          sessionList: newSessionList,
          projectId: sessionProject.projectId,
          projectName: sessionProject.projectId,
          sessionId: sessionProject.sessionId || project.canonicalName,
          projectUUID: sessionProject.projectUUID || project.uuid,
          projectLabel: sessionProject.projectName || project.canonicalName,
        })
      )
      dispatch(removeShortlist())

      const isValidRedirectPath = redirectTo
        ? validateRedirectPath(redirectTo)
        : false
      if (redirectTo && isValidRedirectPath) {
        await firebaseControlQuery.updateProjectRoute({
          activeRoute: redirectTo,
          projectId: sessionProject.projectId,
          sessionId: sessionProject.sessionId,
        })
        window.location.href = `/${redirectTo}`
      }

      if (!redirectTo || !isValidRedirectPath) {
        window.location.reload()
      }

      return {
        status: true,
      }
    },
    [
      projectIdentity,
      getProject,
      getProjectConfig,
      getDefaultRoom,
      setDefaultRoom,
      validateRedirectPath,
      triggerFirebaseProjectSwitch,
    ]
  )

  return {
    startProjectSwitch,
    validateRedirectPath,
  }
}

export default useProjectSwitchUtils
